import { useCloseDeletedSelection } from "../../../components/common/useCloseDeletedSelection";
import CollectionItemActions from "./CollectionItemActions";
import { nextCollectionNumber, useCollectionItems } from "./collectionItems";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../../helpers/formValidationHelper";
import { IFieldEditor, IHTMLBlockItem } from "../../../../models/Interfaces";
import { Core, formValidation, notEmpty, callback } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { Message } from "@form-validation/plugin-message";
import { Form, Button, Col, Row, FormGroup, FormLabel, FormControl, Card, CardTitle, CardBody, CardFooter } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import ColourPicker from "../../../components/common/ColourPicker";
import HTMLEditor from "../../../components/common/HTMLEditor";
import { htmlBlockItemFrame } from "../../../components/common/editorSiteFrame";

const HTMLBlockEditor = (props: IFieldEditor) => {
  const [items, setItems] = useCollectionItems<IHTMLBlockItem>(props);
  const [ selectedItem, setSelectedItem ] = useState<IHTMLBlockItem | null>(null);
  const [ editorOpen, setEditorOpen ] = useState<boolean>(false);

  const [editorSession, setEditorSession] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const addItem = () : void => {
    const item: IHTMLBlockItem = {
      Id: nextCollectionNumber(items),
      Title: "",
      BackgroundColour: "",
      Content: ""
    };
    setSelectedItem(item);
    openCloseEditor(true);
  }

  const editItem = (item: IHTMLBlockItem): void => {
    if (item) {
      setSelectedItem(item);
      openCloseEditor(true);
    }
  };

  const closeEdit = () : void => {
    openCloseEditor(false)
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => openCloseEditor(false));

  const deleteItem = (item: IHTMLBlockItem) : void => {
    const updated = items.filter(i => i.Id !== item.Id);

    if (updated) {
      setItems(updated);
      closeDeletedSelection(item.Id);
    } else {
      setItems([]);
    }
  };

  const saveItem = async () : Promise<void> => {
    
    if (fvRef.current && selectedItem !== null) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        const existing = items.some(itm => itm.Id === selectedItem.Id);
        
        if (existing) {
          const updated = items.map((itm, index) => itm.Id === selectedItem.Id ? selectedItem : itm);
          setItems(updated);
        } else {
          setItems(prev => [
            ...prev,
            selectedItem
          ]);
        }
        openCloseEditor(false);
      }
    }
  };

  const updateProperty = (name: string, value: string) : void => {
    if (name) {
      setSelectedItem(prev => ({
        ...prev as IHTMLBlockItem,
        [name]: value
      }));
      if (name) {
        fvRef.current?.revalidateField(name);
      }
    }
  };


  const openCloseEditor = (open: boolean) : void => {
    if (open) setEditorSession(session => session + 1);
    if (props.onEditorOpen) {
      props.onEditorOpen(open);
    }

    setEditorOpen(open);

    if (!open) {
    }
  };

  const createValidation = () : void => {
    if (!fvRef.current && formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Title: {
            validators: {
              notEmpty: { message: "A title is required for the html block" }
            }
          },
          Content: {
            validators: {
              callback: {
                callback: (field: HTMLTextAreaElement) => {
                  const validator = { valid: false, message: "HTML content is required" };
                  
                  if (field?.value) {
                    validator.valid = true;
                    validator.message = "";
                  }
                  return validator;
                }
              }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton(),
        }
      });
    }
  };


  useEffect(() => {
    if (!editorOpen) return;
    createValidation();
    return () => {
      fvRef.current?.destroy();
      fvRef.current = null;
    };
  }, [editorOpen]);



  return (
    <>
      { !editorOpen && (
        <Row>
          <Col xs={12}>
            <Button 
              variant="outline-primary" 
              title="add a block of html"
              onClick={addItem}>
              <FontAwesomeIcon icon={faPlus} />&nbsp;Add HTML Block
            </Button>
          </Col>
        </Row>
      )}

      <div hidden={editorOpen}>
        <Row>
          {items.map((itm, index) =>
            <Col key={itm.Id} md={12} xl={3} className="mt-3">
              <Card className="h-100 p-2">
                <CardTitle>
                  <h5>{itm.Title}</h5>
                </CardTitle>
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <div dangerouslySetInnerHTML={{__html: itm.Content}}></div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter>
                  <Button variant="outline-secondary" 
                    className="btn-nomin me-1" 
                    title="edit html item"
                    onClick={() => editItem(itm)}>
                      <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button variant="outline-secondary" 
                    className="btn-nomin" 
                    title="remove html item"
                    onClick={() => deleteItem(itm)}>
                      <FontAwesomeIcon icon={faTimes} />
                  </Button>
                  <CollectionItemActions items={items} index={index} onChange={setItems} />
                </CardFooter>
              </Card>
            </Col>
          )}
        </Row>
      </div>
      <div hidden={!editorOpen} data-nested-editor-open={editorOpen ? "true" : undefined}>
        {selectedItem !== null && (
        <Form id="HTMLItemForm" ref={formRef}>
          <Row>
            <Col md={12} xl={6} className="mt-3">
              <FormGroup className="form-group">
                <FormLabel>Item Title</FormLabel>
                <FormControl
                  type="text"
                  title="block title"
                  value={selectedItem?.Title ?? ""}
                  onChange={(e) => updateProperty(e.target.name, e.target.value)}
                  name="Title"
                  placeholder="add a title for your block of html..."
                  id="Title" 
                />
              </FormGroup>
            </Col>
            <Col md={12} xl={6} className="mt-3">
              <FormGroup className="form-group">
                <FormLabel>Background Colour</FormLabel>
                <ColourPicker 
                  onColourChange={(colour) => updateProperty("BackgroundColour", colour ?? "")} 
                  selectedColour={selectedItem?.BackgroundColour} 
                  key={`colourpicker`} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="mt-3">
              <FormGroup className="form-group">
                <FormLabel>Item HTML Content</FormLabel>
                <HTMLEditor documentKey={editorSession} height={props.editorHeight}
                  value={selectedItem?.Content ?? ""}
                  id="Content"
                  name="Content"
                  siteFrame={htmlBlockItemFrame(props.siteFrame, selectedItem?.BackgroundColour)}
                  onChange={(content) => updateProperty("Content", content ?? "")} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <hr />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-end">
              <Button 
                variant="outline-secondary" 
                title="save html item"
                className="me-1"
                onClick={saveItem}>
                  <FontAwesomeIcon icon={faSave} />&nbsp;Save
              </Button>
              <Button 
                variant="outline-primary" 
                title="close editor" 
                onClick={closeEdit}>
                Close
              </Button>
            </Col>
          </Row>
        </Form>
        )}
      </div>
    </>
  );
}

export default HTMLBlockEditor;
